import React from 'react';
import { Box, Typography } from '@mui/material';
import IPhoneFrame, {
  StatusBar, NavBar, HomeBar,
  IOS_FONT, IOS_BLUE, IOS_GRAY_BG, IOS_GRAY_FIELD, IOS_GRAY_TEXT, IOS_SEPARATOR,
} from './IPhoneFrame';
import { CLASS } from '../sharedMockData';

// Shared class-code "CIV5A2" — first 4 chars entered, last 2 unfilled.
const ENTERED = CLASS.code.slice(0, 4).split('');
const PLACEHOLDERS = 6 - ENTERED.length;

const IosJoinClassMockup = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, bgcolor: '#eceff1', minHeight: '100%' }}>
    <IPhoneFrame
      title="5. Join a Class (class-code entry)"
      caption="iOS rounded-rect field, numeric keyboard hint, submit button in iOS blue. Validates against Nucleos roster sync."
    >
      <StatusBar />
      <NavBar
        title="Join a Class"
        leadingButton={{ chevron: true, label: 'Classes' }}
        trailingButton={{ label: 'Help' }}
      />
      <Box sx={{
        position: 'absolute',
        top: 44 + 44, left: 0, right: 0, bottom: 0,
        bgcolor: IOS_GRAY_BG,
        overflow: 'auto',
      }}>
        <Box sx={{ p: 2.5, pt: 3 }}>
          {/* Icon */}
          <Box sx={{
            width: 72, height: 72, borderRadius: '20px',
            bgcolor: IOS_BLUE,
            mx: 'auto', mb: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '36px',
            boxShadow: '0 6px 18px rgba(0, 122, 255, 0.25)',
          }}>🎓</Box>

          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '22px', fontWeight: 700, textAlign: 'center', mb: 1,
          }}>Enter your class code</Typography>
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '14px', color: IOS_GRAY_TEXT,
            textAlign: 'center', mb: 4, px: 2,
          }}>
            Your teacher shared a code. Type it below to join the class and get your
            assignments.
          </Typography>

          {/* Class-code chunked input */}
          <Box sx={{
            display: 'flex', justifyContent: 'center', gap: 1, mb: 1,
          }}>
            {[...ENTERED, ...Array(PLACEHOLDERS).fill('')].map((c, i) => (
              <Box key={i} sx={{
                width: 42, height: 52,
                bgcolor: '#fff',
                border: i === ENTERED.length ? `2px solid ${IOS_BLUE}` : `0.5px solid ${IOS_SEPARATOR}`,
                borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: IOS_FONT, fontSize: '22px', fontWeight: 600,
                color: c ? '#000' : IOS_GRAY_TEXT,
              }}>{c}</Box>
            ))}
          </Box>
          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '12px', color: IOS_GRAY_TEXT,
            textAlign: 'center', mb: 3,
          }}>6 letters or numbers</Typography>

          {/* OR divider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{ flex: 1, height: '0.5px', bgcolor: IOS_SEPARATOR }} />
            <Typography sx={{ fontFamily: IOS_FONT, fontSize: '12px', color: IOS_GRAY_TEXT }}>OR</Typography>
            <Box sx={{ flex: 1, height: '0.5px', bgcolor: IOS_SEPARATOR }} />
          </Box>

          {/* Scan QR */}
          <Box sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            border: `0.5px solid ${IOS_SEPARATOR}`,
            p: 1.8,
            display: 'flex', alignItems: 'center', gap: 1.5, mb: 2,
          }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: 1.5,
              bgcolor: '#F2F2F7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '22px',
            }}>📷</Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontFamily: IOS_FONT, fontSize: '15px', fontWeight: 600 }}>
                Scan QR code
              </Typography>
              <Typography sx={{ fontFamily: IOS_FONT, fontSize: '12px', color: IOS_GRAY_TEXT }}>
                Use the camera to scan your teacher's code
              </Typography>
            </Box>
            <Box sx={{ color: IOS_BLUE, fontSize: '18px', fontWeight: 300 }}>›</Box>
          </Box>

          {/* Submit button (iOS blue) */}
          <Box sx={{
            bgcolor: IOS_BLUE,
            borderRadius: 2.5,
            py: 1.5,
            fontFamily: IOS_FONT, fontSize: '17px', fontWeight: 600,
            color: '#fff', textAlign: 'center', cursor: 'pointer',
            mb: 2,
          }}>Join Class</Box>

          <Typography sx={{
            fontFamily: IOS_FONT, fontSize: '12px', color: IOS_GRAY_TEXT,
            textAlign: 'center',
          }}>
            Can't find a code? Ask your teacher, or <Box component="span" sx={{ color: IOS_BLUE }}>browse available classes</Box>.
          </Typography>
        </Box>

        {/* Fake keyboard region */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          bgcolor: '#D1D5DB',
          py: 1, px: 0.5,
          display: 'flex', flexDirection: 'column', gap: 0.4,
        }}>
          {['1234567890', 'QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'].map((row, ri) => (
            <Box key={ri} sx={{ display: 'flex', justifyContent: 'center', gap: 0.3 }}>
              {row.split('').map((k, i) => (
                <Box key={i} sx={{
                  minWidth: 30, height: 38,
                  bgcolor: '#fff',
                  borderRadius: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: IOS_FONT, fontSize: '18px', fontWeight: 400,
                  boxShadow: '0 1px 0 rgba(0,0,0,0.15)',
                  px: 0.5,
                }}>{k}</Box>
              ))}
            </Box>
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.3 }}>
            <Box sx={{
              minWidth: 80, height: 38, bgcolor: '#AEB3BC',
              borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: IOS_FONT, fontSize: '13px',
            }}>123</Box>
            <Box sx={{
              flex: 1, height: 38, bgcolor: '#fff',
              borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: IOS_FONT, fontSize: '13px',
            }}>space</Box>
            <Box sx={{
              minWidth: 80, height: 38, bgcolor: IOS_BLUE,
              borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: IOS_FONT, fontSize: '13px', color: '#fff', fontWeight: 600,
            }}>go</Box>
          </Box>
        </Box>
      </Box>
      <HomeBar />
    </IPhoneFrame>
  </Box>
);

export default IosJoinClassMockup;
